/**
 * Integrytas Group — Back-button guard
 *
 * The workspace keeps its open tabs in sessionStorage, not in browser history,
 * so a browser Back would otherwise leave the app entirely. This intercepts Back:
 * while any tab is open, Back returns to the menu (closes tabs) instead of leaving;
 * at the menu (no tabs open) Back asks for confirmation before leaving the app.
 *
 * Mechanism: a sentinel history entry is pushed so the first Back pops to it and
 * fires popstate; the handler re-arms the sentinel each time so Back stays
 * captured until the user confirms leaving.
 */
import { useEffect, useRef } from 'react';

interface BackGuardOptions {
  hasTabs: boolean;         // are any workspace tabs open?
  goToMenu: () => void;     // return to the menu (e.g. close all tabs)
  confirmLeaveMessage: string;
}

export function useBackGuard({ hasTabs, goToMenu, confirmLeaveMessage }: BackGuardOptions): void {
  const hasTabsRef = useRef(hasTabs);
  hasTabsRef.current = hasTabs;
  const goToMenuRef = useRef(goToMenu);
  goToMenuRef.current = goToMenu;
  const msgRef = useRef(confirmLeaveMessage);
  msgRef.current = confirmLeaveMessage;

  useEffect(() => {
    const arm = () => window.history.pushState({ csmBackGuard: true }, '');
    arm();
    const onPop = () => {
      if (hasTabsRef.current) {
        // Tabs open → return to the menu and re-arm the guard.
        goToMenuRef.current();
        arm();
      } else if (window.confirm(msgRef.current)) {
        // At the menu and confirmed → let the browser leave the app.
        window.history.back();
      } else {
        // Cancelled → stay, re-arm the guard.
        arm();
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
}
